import { useCallback } from 'react';
import { getDeletesForSegmentPure } from '@goldcast/player-config/src/utils/timeUtils';
import type { ClipDeletes } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { TranscriptPauseEntry } from './types';
import { getPauseDeleteBounds, findPauseDeleteKey, buildPauseDeletesMap } from './pauseUtils';
import { restoreDelete } from './EditTranscriptMenu/utils';
import { useAppContext } from '@/context/AppContext/AppContext';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { currentClip } from '@/stores/clip';

export default function usePauseOperations({
  clipId,
  deletesWithDragChanges
}: {
  clipId: string;
  deletesWithDragChanges: Record<string, ClipDeletes>;
}): {
  onPauseOptimize: (entry: TranscriptPauseEntry) => void;
  onPauseRecover: (entry: TranscriptPauseEntry) => void;
  onBulkOptimize: (entries: TranscriptPauseEntry[]) => void;
  onBulkRecover: (entries: TranscriptPauseEntry[]) => void;
} {
  const { analytics } = useAppContext();

  const onPauseOptimize = useCallback(
    (entry: TranscriptPauseEntry) => {
      const [deleteStart, deleteEnd] = getPauseDeleteBounds(entry);
      const durationReduced = entry.pauseDuration - entry.recommendedPause;

      updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
        startTime: deleteStart,
        startSegmentId: entry.segmentId,
        endTime: deleteEnd,
        endSegmentId: entry.segmentId,
        deleteType: 'PAUSE_OPTIMIZATION'
      });

      analytics.track('ContentLabEvent', {
        name: 'pause_optimization_optimize',
        clipId,
        pauseCount: 1,
        totalDurationReduced: Math.round(durationReduced * 1000) / 1000
      });
    },
    [clipId, analytics]
  );

  const onPauseRecover = useCallback(
    (entry: TranscriptPauseEntry) => {
      const segmentDeletes = deletesWithDragChanges[entry.segmentId] || {};
      const deleteKey = findPauseDeleteKey(entry, segmentDeletes);

      if (!deleteKey) {
        console.error('[Pause Recovery] Could not find delete to remove for pause', entry.segmentId);
        return;
      }

      // Restore only this pause's sub-range, carving it out of whatever delete currently covers it.
      const [deleteStart, deleteEnd] = getPauseDeleteBounds(entry);
      const durationRestored = entry.pauseDuration - entry.recommendedPause;

      updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.RESTORE_COMPOSITE_SEGMENT_DELETE,
        startTime: deleteStart,
        startCompositeId: entry.segmentId,
        endTime: deleteEnd,
        endCompositeId: entry.segmentId
      });

      analytics.track('ContentLabEvent', {
        name: 'pause_optimization_restore',
        clipId,
        pauseCount: 1,
        totalDurationRestored: Math.round(durationRestored * 1000) / 1000
      });
    },
    [clipId, deletesWithDragChanges, analytics]
  );

  const onBulkOptimize = useCallback(
    (entries: TranscriptPauseEntry[]) => {
      if (entries.length === 0) {
        return;
      }

      const entriesBySegment = groupEntriesBySegment(entries);
      let totalDurationReduced = 0;

      for (const [segmentId, segmentEntries] of Object.entries(entriesBySegment)) {
        // Calculate total duration reduction
        totalDurationReduced += segmentEntries.reduce(
          (sum, entry) => sum + (entry.pauseDuration - entry.recommendedPause),
          0
        );

        updateClipData({
          clipId,
          actionType: CLIP_UPDATE_TYPE.BULK_ADD_COMPOSITE_SEGMENT_DELETES,
          deletes: buildPauseDeletesMap(segmentEntries),
          compositeId: segmentId
        });
      }

      analytics.track('ContentLabEvent', {
        name: 'pause_optimization_optimize_all',
        clipId,
        pauseCount: entries.length,
        totalDurationReduced: Math.round(totalDurationReduced * 1000) / 1000
      });
    },
    [clipId, analytics]
  );

  const onBulkRecover = useCallback(
    (entries: TranscriptPauseEntry[]) => {
      if (entries.length === 0) {
        return;
      }

      const freshClip = currentClip.getSnapshot()[clipId];
      if (!freshClip) {
        return;
      }

      const entriesBySegment = groupEntriesBySegment(entries);
      let totalDurationRestored = 0;

      for (const [segmentId, segmentEntries] of Object.entries(entriesBySegment)) {
        // Calculate total duration restoration
        totalDurationRestored += segmentEntries.reduce(
          (sum, entry) => sum + (entry.pauseDuration - entry.recommendedPause),
          0
        );

        let segmentDeletes = structuredClone(
          getDeletesForSegmentPure({
            clip: freshClip,
            segmentId,
            isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
          })
        );

        for (const entry of segmentEntries) {
          const deleteKey = findPauseDeleteKey(entry, segmentDeletes);
          if (!deleteKey) {
            continue;
          }
          // Restore only this pause's sub-range, carving it out of whatever delete currently covers it.
          const [deleteStart, deleteEnd] = getPauseDeleteBounds(entry);
          segmentDeletes = restoreDelete(segmentDeletes, {
            bounds: [deleteStart, deleteEnd],
            prev_end_time: deleteStart,
            next_start_time: deleteEnd
          });
        }

        updateClipData({
          clipId,
          actionType: CLIP_UPDATE_TYPE.BULK_PUT_COMPOSITE_SEGMENT_DELETE,
          deletes: segmentDeletes,
          compositeId: segmentId
        });
      }

      analytics.track('ContentLabEvent', {
        name: 'pause_optimization_restore_all',
        clipId,
        pauseCount: entries.length,
        totalDurationRestored: Math.round(totalDurationRestored * 1000) / 1000
      });
    },
    [clipId, analytics]
  );

  return { onPauseOptimize, onPauseRecover, onBulkOptimize, onBulkRecover };
}

function groupEntriesBySegment(entries: TranscriptPauseEntry[]): Record<string, TranscriptPauseEntry[]> {
  const grouped: Record<string, TranscriptPauseEntry[]> = {};
  for (const entry of entries) {
    if (!grouped[entry.segmentId]) {
      grouped[entry.segmentId] = [];
    }
    grouped[entry.segmentId].push(entry);
  }
  return grouped;
}
